"use client";

import getClassInvites from "@/actions/instructor-page/membership-management/get-class-invites";
import { useChessSocketContext } from "@/app/oyun/_providers/ChessSocketProvider";
import AskidaSuccessModal from "@/components/askida-success-modal";
import { BattleInviteItem } from "@/components/battle-invite-item";
import ClassInviteItem from "@/components/class-invite-item";
import GameInviteItem from "@/components/game-invite-item";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/shadcn/alert-dialog";
import { Button } from "@/components/shadcn/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/shadcn/popover";
import { Separator } from "@/components/shadcn/separator";
import { useNotificationEvents } from "@/hooks/useNotificationEvents";
import { defaultLocale, isLocale } from "@/i18n/config";
import { localizeHref } from "@/i18n/navigation";
import { useSession } from "@/lib/auth/client";
import useStore from "@/store/useStore";
import { Sparkle } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { NotificationBell } from "./notification-bell";

export const NavbarNotification = () => {
    const { data: session } = useSession();
    const rawLocale = useLocale();
    const locale = isLocale(rawLocale) ? rawLocale : defaultLocale;
    const t = useTranslations("nav");
    const isAuthenticated = Boolean(session?.user);
    const isPremium = session?.user?.isPremium === true;
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
    const [message, setMessage] = useState("");
    const [availableGifts, setAvailableGifts] = useState([]);

    const {
        classInvites,
        gameInvites,
        battleInvites,
        setClassInvites,
        removeGameInvite,
        removeBattleInvite,
    } = useStore();

    const { socket } = useChessSocketContext();
    useNotificationEvents();

    // Fetch available gifts
    useEffect(() => {
        fetch("/api/gift-premium/list-available-gifts", {
            method: "POST",
            headers: {
                "Content-type": "application/json; charset=UTF-8",
            },
        })
            .then((response) => {
                if (!response.ok) throw new Error("Failed to fetch gifts");
                return response.json();
            })
            .then((data) => setAvailableGifts(data))
            .catch((error) => console.error("Error fetching gifts:", error));
    }, []);

    // Fetch class invites for authenticated users
    const fetchInvites = useCallback(async () => {
        if (!isAuthenticated) return;

        try {
            const result = await getClassInvites();
            if (result.success) {
                setClassInvites(result.data || []);
            }
        } catch (error) {
            console.error("Error fetching invites:", error);
        }
    }, [isAuthenticated, setClassInvites]);

    useEffect(() => {
        fetchInvites();
    }, [fetchInvites]);

    const handleGiftClick = () => {
        if (!isAuthenticated) {
            window.location.assign(localizeHref("/login", locale));
            return;
        }

        fetch("/api/gift-premium/assign-gift", {
            method: "POST",
            headers: {
                "Content-type": "application/json; charset=UTF-8",
            },
        })
            .then((response) => {
                if (response.status === 404) {
                    setAvailableGifts([]);
                    return null;
                }
                if (!response.ok) throw new Error("Failed to assign gift");
                return response.json();
            })
            .then((data) => {
                if (!data) return;
                if (data && data.message !== "") {
                    setAlertDialogOpen(true);
                    setMessage(data.message);
                } else {
                    setModalOpen(true);
                }
            })
            .catch((error) => console.error("Error assigning gift:", error));
    };

    const isNotPremium = !isPremium;
    const hasGifts = availableGifts.length > 0;
    const hasInvites = classInvites.length > 0;
    const hasGameInvites = gameInvites.length > 0;
    const hasBattleInvites = battleInvites.length > 0;

    const showGiftNotification = hasGifts && (!isAuthenticated || isNotPremium);
    const showNotificationDot =
        showGiftNotification ||
        hasInvites ||
        hasGameInvites ||
        hasBattleInvites;

    return (
        <>
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative"
                        aria-label={t("notifications")}
                    >
                        <NotificationBell
                            hasNotification={showNotificationDot}
                        />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-1">
                    {showGiftNotification && (
                        <div className="flex flex-col gap-1">
                            <Button
                                variant="ghost"
                                className="justify-start"
                                onClick={handleGiftClick}
                            >
                                <Sparkle data-icon="inline-start" />
                                {t("giftAvailable")}
                            </Button>
                            <Separator />
                        </div>
                    )}

                    {!isAuthenticated ||
                    (!hasInvites && !hasGameInvites && !hasBattleInvites) ? (
                        <p className="text-muted-foreground p-2 text-sm">
                            {t("noNotifications")}
                        </p>
                    ) : (
                        <div className="flex max-h-96 flex-col gap-3 overflow-y-auto p-2">
                            {battleInvites.map((invite) => (
                                <BattleInviteItem
                                    key={invite.battleId}
                                    invite={invite}
                                    onRemove={removeBattleInvite}
                                />
                            ))}

                            {gameInvites.map((invite) => (
                                <GameInviteItem
                                    key={invite.inviteId}
                                    invite={invite}
                                    socket={socket}
                                    onRemove={removeGameInvite}
                                />
                            ))}

                            {classInvites.map((invite) => (
                                <ClassInviteItem
                                    key={invite.id}
                                    invite={invite}
                                    onActionComplete={fetchInvites}
                                />
                            ))}
                        </div>
                    )}
                </PopoverContent>
            </Popover>

            <AskidaSuccessModal isOpen={modalOpen} />

            <AlertDialog
                open={alertDialogOpen}
                onOpenChange={setAlertDialogOpen}
            >
                <AlertDialogTrigger className="hidden">Open</AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("info")}</AlertDialogTitle>
                        <AlertDialogDescription>{`${message}`}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("ok")}</AlertDialogCancel>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
